import React, { useState, useEffect, useCallback } from 'react';
import { useTeamStore } from '../../store/teamStore';
import { RefreshCw, ClipboardList, Printer } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { NoTeamsGate } from '../ui/NoTeamsGate';

// Components
import FormationDepthChart from './FormationDepthChart';
import DepthChartMobile from './DepthChartMobile';
import DepthChartTable from './DepthChartTable';
import DepthStringManager from './DepthStringManager';
import PageHeader from '../layout/PageHeader';
import RandomizationModal from '../RandomizationModal';
import FormationBuilderManager from '../formation-builder/FormationBuilderManager';
import { DepthChartControls } from './DepthChartControls';

// Hooks
import { useDepthChart } from '../../hooks/useDepthChart';
import { useDepthChartRandomizer } from '../../hooks/useDepthChartRandomizer';
import { useFormations } from '../../hooks/useFormations';
import { useDepthStrings } from '../../hooks/useDepthStrings';
import { useDepthChartPrint } from '../../hooks/useDepthChartPrint';
import { usePositionEditor } from '../../hooks/usePositionEditor';
import { useDepthChartModals } from '../../hooks/useDepthChartModals';

// Data and Types
import { SPORT_RULESETS, DepthChartTab } from '../../data/sportRulesets';
import { Position } from '../../types';
import { Position as CustomPosition } from '../../types/depth-chart';
import type { Sport } from '../../types';
import { supabase } from '../../lib/supabase';
import type { FormationPosition } from '../formation-builder/VisualFormationBuilder';
import { BaseballLineupView } from './BaseballLineupView';
import { BaseballDiamondFieldingView } from './BaseballDiamondFieldingView';
import OptimizeLineupModal from '../OptimizeLineupModal';
import { sportStatsService } from '../../services/sportStatsService';
import { getCalculatedStats } from '../../utils/statCalculations';
import { playerCardService } from '../../services/playerCardService';

export const DepthChartContainer: React.FC = () => {
  const { currentTeam, allTeams, fetchAllTeams } = useTeamStore();
  
  // Basic state
  const [activeTab, setActiveTab] = useState<string>('Offense');
  const [activeDepth, setActiveDepth] = useState<number>(1);
  const [isLoadingTeams, setIsLoadingTeams] = useState(true);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [currentFormationId, setCurrentFormationId] = useState<string | undefined>(undefined);
  
  // Sport-specific configuration
  const [sportRuleset, setSportRuleset] = useState<typeof SPORT_RULESETS.Football | null>(null);
  const [availableTabs, setAvailableTabs] = useState<DepthChartTab[]>([]);
  
  // Baseball/Softball innings state
  const [currentInning, setCurrentInning] = useState<number>(1);
  const [totalInnings, setTotalInnings] = useState<number>(9);
  
  // Optimize modal state
  const [showOptimizeModal, setShowOptimizeModal] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  // Player stats for baseball/softball (calculated from sport_stats)
  const [playerStatsMap, setPlayerStatsMap] = useState<Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>>(new Map());
  
  // Custom hooks
  const formations = useFormations({ currentTeam, activeTab, sportRuleset });
  const depthStrings = useDepthStrings({ currentTeam, activeTab });
  const depthChartHook = useDepthChart({ currentTeam, activeTab, formations });
  const modals = useDepthChartModals();
  
  // Position editor hook
  const positionEditor = usePositionEditor({
    onSaveCustomPositionLabel: depthChartHook.saveCustomPositionLabel,
    onPositionColorChange: depthChartHook.handlePositionColorChange
  });
  
  // Print functionality hook
  const { handlePrint } = useDepthChartPrint({
    currentTeam,
    sportRuleset,
    depthChart: depthChartHook.depthChart,
    players: depthChartHook.players,
    depthStrings: depthStrings.depthStrings,
    customPositions: depthChartHook.customPositions
  });
  
  // Randomization hook
  const randomizer = useDepthChartRandomizer({
    players: depthChartHook.players,
    positions: depthChartHook.positions,
    currentDepthChart: depthChartHook.depthChart,
    activeTab,
    sport: currentTeam?.sport || 'Football',
    teamId: currentTeam?.id || '',
    team: currentTeam || undefined,
    fieldingInnings:
      (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && activeTab === 'Fielding'
        ? totalInnings
        : undefined,
    selectedFormation: formations.selectedFormation,
    onRandomizationComplete: (entries) => {
      console.log('[DepthChartContainer] onRandomizationComplete called with', entries.length, 'entries');
      console.log('[DepthChartContainer] Sample entry:', entries[0]);
      console.log('[DepthChartContainer] Current depthChart entries before update:', depthChartHook.depthChart.length);
      
      const otherTabEntries = depthChartHook.depthChart.filter(entry => entry.unit !== activeTab);
      console.log('[DepthChartContainer] Other tab entries to preserve:', otherTabEntries.length);
      
      const newDepthChart = [...otherTabEntries, ...entries];
      console.log('[DepthChartContainer] New depth chart total entries:', newDepthChart.length);
      
      depthChartHook.setDepthChart(newDepthChart);
      console.log('[DepthChartContainer] Called setDepthChart, now calling saveDepthChartWithData directly');
      
      // Use saveDepthChartWithData to avoid timing issues with state updates
      depthChartHook.saveDepthChartWithData(newDepthChart);
    }
  });

  // Load teams on component mount
  useEffect(() => {
    const loadTeams = async () => {
      setIsLoadingTeams(true);
      await fetchAllTeams();
      setIsLoadingTeams(false);
    };
    loadTeams();
  }, [fetchAllTeams]);

  // Refetch depth chart when it was updated elsewhere (e.g. Coach Steve assign/move)
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ teamId?: string }>).detail;
      if (!currentTeam?.id || (detail?.teamId && detail.teamId !== currentTeam.id)) return;
      depthChartHook.loadDepthChart();
    };
    window.addEventListener('depth-chart-updated', handler);
    return () => window.removeEventListener('depth-chart-updated', handler);
  }, [currentTeam?.id, depthChartHook.loadDepthChart]);

  // Initialize sport ruleset and tabs when team changes
  useEffect(() => {
    if (currentTeam) {
      const ruleset = SPORT_RULESETS[currentTeam.sport];
      setSportRuleset(ruleset);
      setAvailableTabs(ruleset.tabs);
      
      const firstTab = ruleset.tabs[0].id;
      setActiveTab(firstTab);
      
      if (ruleset.useInnings) {
        // Use team's innings setting if available, otherwise use ruleset default
        setTotalInnings(currentTeam.innings || ruleset.defaultInnings || 9);
        setCurrentInning(1);
      }
      
      // Check screen size for mobile view
      const checkMobile = () => setIsMobile(window.innerWidth < 768);
      checkMobile();
      window.addEventListener('resize', checkMobile);
      
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, [currentTeam?.id, currentTeam?.sport]);

  // Load player stats for baseball/softball
  useEffect(() => {
    const loadPlayerStats = async () => {
      if (!currentTeam?.id || (currentTeam.sport !== 'Baseball' && currentTeam.sport !== 'Softball')) {
        setPlayerStatsMap(new Map());
        return;
      }

      try {
        // Get all player cards for the team
        const playerCards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
        
        // Load stats for each player card
        const statsMap = new Map<string, { batting_average?: number; on_base_percentage?: number; batting_hand?: 'L' | 'R' | 'S' }>();
        
        for (const card of playerCards) {
          if (!card.player?.id) continue;
          
          // Get batting hand from player card
          const battingHand = card.batting_hand || card.player.batting_hand;
          
          // Load all stats for this player card
          const allStats = await sportStatsService.getStats(card.id, currentTeam.id);
          
          if (allStats.length > 0) {
            // Calculate stats from raw stats
            const rawStats = allStats.map((s: { stat_key: string; stat_value: number }) => ({ stat_key: s.stat_key, stat_value: s.stat_value }));
            const calculated = getCalculatedStats(currentTeam.sport, rawStats, currentTeam.innings);
            
            statsMap.set(card.player.id, {
              batting_average: calculated.AVG,
              on_base_percentage: calculated.OBP,
              batting_hand: battingHand as 'L' | 'R' | 'S' | undefined,
            });
          } else if (battingHand) {
            // No stats yet, but we have batting hand
            statsMap.set(card.player.id, {
              batting_hand: battingHand as 'L' | 'R' | 'S' | undefined,
            });
          }
        }
        
        setPlayerStatsMap(statsMap);
      } catch (error) {
        console.error('Error loading player stats:', error);
      }
    };

    loadPlayerStats();
  }, [currentTeam?.id, currentTeam?.sport, depthChartHook.players.length]);

  // Update totalInnings when team's innings setting changes (for baseball/softball)
  useEffect(() => {
    if (currentTeam && (currentTeam.sport === 'Baseball' || currentTeam.sport === 'Softball')) {
      if (currentTeam.innings) {
        setTotalInnings(currentTeam.innings);
      }
    }
  }, [currentTeam?.innings, currentTeam?.sport]);

  // Set default formation when data is loaded (only if formations are available)
  useEffect(() => {
    if (!currentTeam || !sportRuleset || depthChartHook.isLoading || formations.selectedFormation) {
      return;
    }
    
    // For Special Teams, formations are always available (predefined), so check availableFormations instead
    const isSpecialTeams = activeTab === 'Special Teams';
    const availableFormations = formations.getAvailableFormations();
    const hasFormations = isSpecialTeams 
      ? availableFormations.length > 0 
      : formations.teamSelectedFormations.length > 0;
    
    if (hasFormations) {
      formations.setDefaultFormation((positions: Position[]) => {
        depthChartHook.initializeDepthChart(positions);
      });
    }
  }, [currentTeam, sportRuleset, activeTab, formations.teamSelectedFormations, formations.selectedFormation, formations, depthChartHook, depthStrings.depthStrings, depthChartHook.isLoading]);

  // Event handlers
  const handleDepthChange = useCallback((depth: number) => {
    setActiveDepth(depth);
    depthChartHook.handleDepthChange(depth);
  }, [depthChartHook]);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
    formations.setSelectedFormation('');
  }, [formations]);

  const handleFormationChange = useCallback(async (formationName: string) => {
    // Get formation ID if it's a custom formation
    if (currentTeam && formations.customFormations.some(f => f.name === formationName)) {
      try {
        const { data } = await supabase
          .from('custom_formations')
          .select('id')
          .eq('team_id', currentTeam.id)
          .eq('unit', activeTab)
          .eq('name', formationName)
          .single();
        setCurrentFormationId(data?.id);
      } catch (error) {
        console.error('Error fetching formation ID:', error);
        setCurrentFormationId(undefined);
      }
    } else {
      setCurrentFormationId(undefined);
    }

    formations.handleFormationChange(formationName, (positions: Position[]) => {
      depthChartHook.initializeDepthChart(positions);
    });
  }, [formations, depthChartHook, activeTab, currentTeam]);

  // Store initial formation data for editing predefined formations
  const [editingFormationData, setEditingFormationData] = useState<{
    positions: FormationPosition[];
    name: string;
  } | null>(null);

  const handleEditCustomFormation = useCallback(async (formationName: string) => {
    if (!currentTeam) return;
    
    try {
      // IMPORTANT: Check for custom formation FIRST, before checking predefined
      // This ensures that if a user has created a custom formation with the same name
      // as a predefined one, we edit the custom version, not the predefined one
      console.log('[handleEditCustomFormation] Looking for custom formation:', { formationName, team_id: currentTeam.id, sport: currentTeam.sport, unit: activeTab });
      
      // First, let's see all custom formations for debugging
      const { data: allCustomFormations } = await supabase
        .from('custom_formations')
        .select('id, name, sport, unit')
        .eq('team_id', currentTeam.id)
        .eq('sport', currentTeam.sport)
        .eq('unit', activeTab);
      console.log('[handleEditCustomFormation] All custom formations for this team/unit:', allCustomFormations);
      
      // Try to find custom formation - handle errors gracefully
      // If multiple exist, get the most recent one
      let customFormation = null;
      try {
        const { data, error } = await supabase
          .from('custom_formations')
          .select('id, name')
          .eq('team_id', currentTeam.id)
          .eq('sport', currentTeam.sport)
          .eq('unit', activeTab)
          .eq('name', formationName)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) {
          // Log but don't fail - might be a query format issue
          console.warn('[handleEditCustomFormation] Query error (non-fatal):', error);
        } else {
          customFormation = data;
          console.log('[handleEditCustomFormation] Query result:', data);
        }
      } catch (err) {
        console.error('[handleEditCustomFormation] Exception fetching custom formation:', err);
      }

      // If we found a custom formation with an ID, use it
      if (customFormation?.id) {
        console.log('[handleEditCustomFormation] Found custom formation with ID:', customFormation.id, 'name:', customFormation.name);
        setEditingFormationData(null); // Clear any initial data
        modals.setEditingFormationId(customFormation.id);
        modals.openCustomFormationBuilder();
        return;
      } else {
        console.log('[handleEditCustomFormation] No custom formation found for:', formationName, '- will use predefined if available');
      }
      
      // Only if no custom formation exists, check if this is a predefined formation
      const predefinedFormations = formations.getFormationsForSportAndTab(currentTeam.sport, activeTab);
      const isPredefined = predefinedFormations.some(f => f.name === formationName);
      
      if (isPredefined) {
        console.log('[handleEditCustomFormation] No custom formation found, using predefined:', formationName);
        // For predefined formations, convert to FormationPosition format and open builder directly
        const predefinedFormation = predefinedFormations.find(f => f.name === formationName);
        if (predefinedFormation) {
          // Convert to FormationPosition format
          const positions: FormationPosition[] = predefinedFormation.positions.map((pos, index) => {
            // Try to infer position group
            let positionGroupKey: string | undefined;
            if (pos.key === 'QB') positionGroupKey = 'QB';
            else if (['RB', 'FB', 'HB'].includes(pos.key)) positionGroupKey = 'RB';
            else if (['X', 'Z', 'Y', 'SL', 'H', 'WR'].includes(pos.key)) positionGroupKey = 'WR';
            else if (['Y', 'TE', 'U'].includes(pos.key)) positionGroupKey = 'TE';
            else if (['LT', 'LG', 'C', 'RG', 'RT'].includes(pos.key)) positionGroupKey = 'OL';
            
            // Try to get x/y from predefined formation if available, otherwise use defaults
            const defaultX = 50;
            const defaultY = 50;
            
            return {
              id: `pos_${index}_${Date.now()}`,
              label: pos.name.toUpperCase(),
              fullName: pos.fullName,
              x: (pos as any).x || defaultX,
              y: (pos as any).y || defaultY,
              positionGroupKey,
              color: pos.color || '#1E40AF',
              isCenter: pos.key === 'C'
            };
          });
          
          // Store the initial data and open builder directly
          setEditingFormationData({ positions, name: formationName });
          modals.setEditingFormationId(null);
          modals.openCustomFormationBuilder();
          return;
        }
      }
      
      // Clear editing formation data for custom formations
      setEditingFormationData(null);

      // Fallback: try to load from depth_chart (old format)
      const { data: formationData, error } = await supabase
        .from('depth_chart')
        .select('position, position_full_name, color, order_index')
        .eq('team_id', currentTeam.id)
        .eq('unit', activeTab)
        .eq('formation', formationName)
        .eq('is_template', false)
        .order('order_index');

      if (error) throw error;

      if (formationData && formationData.length > 0) {
        const positions: CustomPosition[] = formationData.map(item => ({
          key: item.position,
          name: item.position,
          fullName: item.position_full_name || item.position,
          color: item.color || ''
        }));

        modals.setEditingCustomFormation({ name: formationName, positions });
        modals.openCustomFormationBuilder();
      }
    } catch (error) {
      console.error('Error loading custom formation for editing:', error);
      toast.error('Failed to load formation for editing');
    }
  }, [currentTeam, activeTab, modals, formations]);

  const deleteCustomPosition = async (position: Position) => {
    console.log('Delete custom position:', position);
  };

  // Loading states
  if (isLoadingTeams) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading teams...</p>
      </div>
    );
  }

  if (allTeams.length === 0) {
    return <NoTeamsGate />;
  }

  if (!currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="caption">Loading team data...</p>
      </div>
    );
  }

  // Formation wizard component (shown when no formations are selected)
  const FormationWizard = () => (
    <div className="py-12 text-center bg-white rounded-lg shadow p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Get Started with Formations</h2>
      <p className="text-gray-600 mb-6">You need to select or create a formation before you can set up your depth chart.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <button
          onClick={modals.openCustomFormationBuilder}
          className="btn btn-primary btn-lg"
        >
          Create or Select Formation
        </button>
      </div>
      {sportRuleset?.formations.sportSpecificFormations && (
        <p className="text-sm text-gray-500 mt-4">
          You can choose from sample formations or create your own custom formation
        </p>
      )}
    </div>
  );

  // Formation prompt component (shown when formation is selected but no positions)
  const FormationPrompt = () => (
    <div className="py-8 text-center bg-white rounded-lg shadow p-3">
      <p className="text-lg font-medium text-gray-700 mb-2">No positions to display</p>
      <p className="text-gray-500 mb-4">Select a formation from the dropdown to see available positions.</p>
      {sportRuleset?.formations.sportSpecificFormations && (
        <p className="text-gray-500">
          Don't see a formation you need? 
          <button
            onClick={modals.openCustomFormationBuilder}
            className="ml-2 text-primary hover:underline"
          >
            Create your own!
          </button>
        </p>
      )}
    </div>
  );

  const nonFormationChartCommonProps = {
    positions: depthChartHook.positions,
    depthChart: depthChartHook.depthChart,
    players: depthChartHook.players,
    activeTab,
    activeDepth,
    depthStrings: depthStrings.depthStrings[activeTab] || [],
    editingPosition: positionEditor.editingPosition,
    editedPositionName: positionEditor.editedPositionName,
    setEditedPositionName: positionEditor.setEditedPositionName,
    savePositionEdit: () => {
      void positionEditor.savePositionEdit();
    },
    showTooltip: positionEditor.showTooltip,
    setShowTooltip: positionEditor.setShowTooltip,
    deleteCustomPosition: (position: Position) => {
      void deleteCustomPosition(position);
    },
    handlePositionEdit: positionEditor.handlePositionEdit,
    handlePlayerChange: (position: string, depth: number, playerId: string | null, inning?: number) => {
      void depthChartHook.handlePlayerChange(position, depth, playerId, inning);
    },
    coloringPosition: positionEditor.coloringPosition,
    setColoringPosition: positionEditor.setColoringPosition,
    handlePositionColorChange: (positionKey: string, depth: number, color: string, inning?: number) => {
      void depthChartHook.handlePositionColorChange(positionKey, depth, color, inning);
    },
    currentInning,
    isBaseballFielding: false,
    playerSortBy: currentTeam?.playerSortBy || 'number',
    editedPositionFullName: positionEditor.editedPositionFullName,
    setEditedPositionFullName: positionEditor.setEditedPositionFullName,
  };

  return (
    <div className="container mx-auto px-4 py-4">
      <PageHeader 
        title="Lineup"
        icon={<ClipboardList className="h-6 w-6" />}
        actions={
          <button
            onClick={handlePrint}
            className="btn-secondary flex items-center gap-1 px-2 py-1 text-sm"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        }
      />
      
      <div className="flex flex-col space-y-2">
        {/* Controls */}
        <DepthChartControls
          availableTabs={availableTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          sportRuleset={sportRuleset}
          selectedFormation={formations.selectedFormation}
          availableFormations={formations.getAvailableFormations()}
          onFormationChange={handleFormationChange}
          onCreateFormation={modals.openCustomFormationBuilder}
          onManageFormations={() => {
            // Always allow editing - if it's a predefined formation, we'll create a copy
            if (formations.selectedFormation) {
              handleEditCustomFormation(formations.selectedFormation);
            } else {
              modals.openCustomFormationBuilder();
            }
          }}
          hasCustomFormations={formations.customFormations.length > 0}
          isDefaultFormation={formations.selectedFormation ? formations.isDefaultFormation(formations.selectedFormation) : false}
          onSetAsDefault={(formationName: string, isDefault: boolean) => {
            formations.setFormationAsDefault(formationName, isDefault);
          }}
          depthStrings={depthStrings.depthStrings[activeTab] || []}
          activeDepth={activeDepth}
          onDepthChange={handleDepthChange}
          onManageDepthStrings={modals.openDepthStringManager}
          currentTeam={currentTeam}
          currentInning={currentInning}
          totalInnings={totalInnings}
          onInningChange={setCurrentInning}
        />

        {/* Main Content Area */}
        <div className="bg-white rounded-lg shadow p-2 sm:p-3">
          {depthChartHook.isLoading ? (
            <div className="flex justify-center my-4">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (activeTab === 'Lineup' || activeTab === 'Fielding') ? (
            /* Baseball/Softball specific views - no formation needed */
            activeTab === 'Lineup' ? (
              <BaseballLineupView
                players={depthChartHook.players}
                depthChart={depthChartHook.depthChart}
                activeTab={activeTab}
                onPlayerChange={(battingOrder, playerId, meta) => {
                  depthChartHook.handlePlayerChange(battingOrder.toString(), 1, playerId, undefined, meta);
                }}
                playerSortBy="number"
                onRandomizeClick={randomizer.showRandomizationModal}
                onOptimizeClick={() => setShowOptimizeModal(true)}
                playerStatsMap={playerStatsMap}
              />
            ) : (
              <BaseballDiamondFieldingView
                players={depthChartHook.players}
                depthChart={depthChartHook.depthChart}
                sport={currentTeam.sport}
                activeTab={activeTab}
                currentInning={currentInning}
                onPlayerChange={(position, inning, playerId) => {
                  depthChartHook.handlePlayerChange(position, inning, playerId);
                }}
                playerSortBy="number"
                onRandomizeClick={randomizer.showRandomizationModal}
                totalInnings={totalInnings}
                onAddInning={() => setTotalInnings(totalInnings + 1)}
              />
            )
          ) : !sportRuleset?.formations.enabled ? (
            <>
              {/* Sports without formations (e.g., Basketball) still need a standard positions/depth chart UI. */}
              {isMobile ? (
                <DepthChartMobile
                  {...nonFormationChartCommonProps}
                  cancelPositionEdit={positionEditor.cancelPositionEdit}
                />
              ) : (
                <DepthChartTable
                  {...nonFormationChartCommonProps}
                  cancelPositionEdit={positionEditor.cancelPositionEdit}
                  disablePositionEditing={true}
                />
              )}
            </>
          ) : (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') ? (
            /* Baseball/Softball - no formation needed, show empty state if needed */
            null
          ) : (() => {
            // For Special Teams, check availableFormations instead of teamSelectedFormations
            const isSpecialTeams = activeTab === 'Special Teams';
            const availableFormations = formations.getAvailableFormations();
            const hasFormations = isSpecialTeams 
              ? availableFormations.length > 0 
              : formations.teamSelectedFormations.length > 0;
            return !hasFormations;
          })() ? (
            <FormationWizard />
          ) : (
            <>
              {/* Always use new FormationDepthChart UI */}
              {formations.selectedFormation ? (
                <FormationDepthChart
                  teamId={currentTeam.id}
                  sport={currentTeam.sport as Sport}
                  unit={activeTab as 'Offense' | 'Defense' | 'Special Teams'}
                  formationId={currentFormationId}
                  formationName={formations.selectedFormation}
                  players={depthChartHook.players}
                  onPlayerAssign={(positionLabel, depth, playerId) => {
                    depthChartHook.handlePlayerChange(positionLabel, depth, playerId);
                  }}
                  currentDepthChart={depthChartHook.depthChart.map(e => ({
                    id: e.id,
                    player_id: e.player_id,
                    position: e.position,
                    depth: e.depth,
                    color: e.color
                  }))}
                  depthStrings={depthStrings.depthStrings[activeTab] || []}
                  activeDepth={activeDepth}
                />
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p className="text-lg font-medium mb-2">No formation selected</p>
                  <p className="text-sm">Please select a formation from the dropdown</p>
                </div>
              )}
            </>
          )}
        </div>


        {/* Formation Prompt - Don't show for Baseball/Softball */}
        {depthChartHook.positions.length === 0 && 
          currentTeam?.sport !== 'Baseball' && 
          currentTeam?.sport !== 'Softball' && 
          <FormationPrompt />}
      </div>

      {/* Modals */}
      {modals.showDepthStringManager && (
        <DepthStringManager
          activeTab={activeTab}
          depthStrings={depthStrings.depthStrings[activeTab] || []}
          editingDepthString={depthStrings.editingDepthString}
          editedDepthStringName={depthStrings.editedDepthStringName}
          setEditedDepthStringName={depthStrings.setEditedDepthStringName}
          handleDepthStringEdit={depthStrings.handleDepthStringEdit}
          saveDepthStringEdit={depthStrings.saveDepthStringEdit}
          toggleDepthStringActive={depthStrings.toggleDepthStringActive}
          addNewDepthString={depthStrings.addNewDepthString}
          onClose={modals.closeDepthStringManager}
        />
      )}

      <RandomizationModal
        isOpen={randomizer.showRandomizeModal}
        onClose={() => randomizer.setShowRandomizeModal(false)}
        onConfirm={randomizer.executeRandomization}
        isRandomizing={randomizer.isRandomizing}
        settings={randomizer.randomizationSettings}
        onSettingsChange={randomizer.updateRandomizationSettings}
        preview={randomizer.randomizationPreview}
        title={randomizer.randomizeButtonText}
        description={randomizer.randomizeDescription}
        sport={currentTeam?.sport || 'Football'}
        activeTab={activeTab}
        players={depthChartHook.players}
        positions={depthChartHook.positions}
        teamInnings={
          // For baseball/softball fielding, use totalInnings (which can include extra innings)
          // Otherwise use the team's innings setting
          (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && activeTab === 'Fielding'
            ? totalInnings
            : randomizer.teamInnings
        }
      />

      {/* Optimize Lineup Modal */}
      {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && activeTab === 'Lineup' && (
          <OptimizeLineupModal
            isOpen={showOptimizeModal}
            onClose={() => setShowOptimizeModal(false)}
            playerStatsMap={playerStatsMap}
          onConfirm={async (optimizeSettings) => {
            setIsOptimizing(true);
            try {
              // Get available players (excluding absent)
              const availablePlayers = depthChartHook.players.filter(
                p => !(optimizeSettings.absentPlayerIds || []).includes(p.id)
              );

              // Calculate optimal lineup based on strategy
              const playersWithStats = availablePlayers.map(player => {
                const stats = playerStatsMap.get(player.id);
                return {
                  player,
                  avg: stats?.batting_average || player.batting_average || 0,
                  obp: stats?.on_base_percentage || player.on_base_percentage || 0,
                  hand: stats?.batting_hand || player.batting_hand || 'R'
                };
              });

              let sorted: typeof playersWithStats = [];

              switch (optimizeSettings.strategy) {
                case 'obp_avg':
                  sorted = [...playersWithStats].sort((a, b) => {
                    if (b.obp !== a.obp) return b.obp - a.obp;
                    return b.avg - a.avg;
                  });
                  break;
                case 'power':
                  sorted = [...playersWithStats].sort((a, b) => b.avg - a.avg);
                  break;
                case 'contact':
                  sorted = [...playersWithStats].sort((a, b) => b.obp - a.obp);
                  break;
                case 'balanced':
                  sorted = [...playersWithStats].sort((a, b) => {
                    const aAdvantage = optimizeSettings.opposingPitcherHand && 
                      ((optimizeSettings.opposingPitcherHand === 'L' && a.hand === 'R') || 
                       (optimizeSettings.opposingPitcherHand === 'R' && a.hand === 'L')) ? 1.1 : 1;
                    const bAdvantage = optimizeSettings.opposingPitcherHand && 
                      ((optimizeSettings.opposingPitcherHand === 'L' && b.hand === 'R') || 
                       (optimizeSettings.opposingPitcherHand === 'R' && b.hand === 'L')) ? 1.1 : 1;
                    const aScore = (a.obp * 0.6 + a.avg * 0.4) * aAdvantage;
                    const bScore = (b.obp * 0.6 + b.avg * 0.4) * bAdvantage;
                    return bScore - aScore;
                  });
                  break;
              }

              // If pitcher hand specified and not balanced, adjust for hand advantage
              if (optimizeSettings.opposingPitcherHand && optimizeSettings.strategy !== 'balanced') {
                sorted = sorted.map(p => {
                  const advantage = (optimizeSettings.opposingPitcherHand === 'L' && p.hand === 'R') || 
                                   (optimizeSettings.opposingPitcherHand === 'R' && p.hand === 'L');
                  return { ...p, advantage };
                }).sort((a, b) => {
                  let comparison = 0;
                  if (optimizeSettings.strategy === 'obp_avg') {
                    if (b.obp !== a.obp) comparison = b.obp - a.obp;
                    else comparison = b.avg - a.avg;
                  } else if (optimizeSettings.strategy === 'power') {
                    comparison = b.avg - a.avg;
                  } else {
                    comparison = b.obp - a.obp;
                  }
                  
                  if (Math.abs(comparison) < 0.05) {
                    if (a.advantage && !b.advantage) return -1;
                    if (!a.advantage && b.advantage) return 1;
                  }
                  return comparison;
                });
              }

              // Clear all existing lineup assignments for this unit at depth 1
              const existingLineupEntries = depthChartHook.depthChart.filter(
                (entry) => entry.unit === 'Lineup' && entry.depth === 1
              );
              for (const entry of existingLineupEntries) {
                await depthChartHook.handlePlayerChange(entry.position, 1, null);
              }

              // Assign players to batting order (await each so we don't race or duplicate)
              const maxSlots =
                optimizeSettings.lineupSizeMode === 'top9'
                  ? Math.min(availablePlayers.length, 9)
                  : availablePlayers.length;

              const lineupToApply = sorted.slice(0, maxSlots);
              for (let index = 0; index < lineupToApply.length; index++) {
                await depthChartHook.handlePlayerChange(
                  (index + 1).toString(),
                  1,
                  lineupToApply[index].player.id
                );
              }

              setShowOptimizeModal(false);
            } catch (error) {
              console.error('Error optimizing lineup:', error);
            } finally {
              setIsOptimizing(false);
            }
          }}
          isOptimizing={isOptimizing}
          players={depthChartHook.players}
          currentLineup={[]}
        />
      )}
      
      {modals.showCustomFormationBuilder && (
        <FormationBuilderManager
          unit={activeTab as 'Offense' | 'Defense' | 'Special Teams'}
          onClose={() => {
            setEditingFormationData(null); // Clear editing data when closing
            modals.closeCustomFormationBuilder();
          }}
          onFormationCreated={async () => {
            // Refresh formations list after creation
            await formations.fetchCustomFormations();
            // Refresh team formations to include the new one
            await formations.fetchTeamFormations();
            setEditingFormationData(null); // Clear editing data
            modals.closeCustomFormationBuilder();
            toast.success('Formation created successfully!');
          }}
          editingFormationId={modals.editingFormationId || undefined}
          initialPositions={editingFormationData?.positions}
          initialFormationName={editingFormationData?.name}
        />
      )}
    </div>
  );
};